// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./utils";
import type {
  DocumentWithRelations,
  Document,
  File,
  Notarization,
  NotarizationPostRequest,
  DocumentFilePostRequest,
} from "../../../api/src/types";
import { questionnaireApi } from "./questionnaire";
import { usersApi } from "./users";
import type { Section, Clause, User } from "../../../api/src/types";
import type { DocumentHistoryResponse } from "../../../api/src/documents/history";
import type {
  ApprovalGrantSuccessfulResponse,
  ApprovalRescindSuccessfulResponse,
} from "../../../api/src/documents/approval";
import type { GetRegenerationStatusResponse } from "../../../api/src/documents/regeneration-status";
import type {
  AnalyzeLlmResponse,
  GetLatestLlmRunResponse,
  ResetGenerationEligibleResponse,
  ResetGenerationResponse,
} from "../../../api/src/documents/route";
import type { ClearGenerationTimestampsResponse } from "../../../api/src/admin/users/documents/route";
import type { DocumentNotarizationsGetResponse } from "../../../api/src/documents/notarizations/route";
import type { UserInvitesGetResponse } from "../../../api/src/users/route";

export const documentsApi = createApi({
  reducerPath: "documentsApi",
  baseQuery,
  tagTypes: [
    "documents",
    "files",
    "notarizations",
    "document-modified",
    "invites",
    "document-history",
    "regeneration-status",
    "llm-runs",
    "AdminUsers",
  ],
  endpoints: (builder) => ({
    createDocument: builder.mutation<Document, Partial<Document>>({
      query: (document) => ({
        url: `documents`,
        method: "POST",
        body: document,
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            questionnaireApi.util.invalidateTags([
              "QuestionnaireSections",
              "QuestionGroup",
            ]),
          );
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["documents"],
    }),
    getDocument: builder.query<DocumentWithRelations, string>({
      query: (id) => ({ url: `documents/${id}` }),
      providesTags: ["documents"],
    }),
    getDocumentsByUserId: builder.query<DocumentWithRelations[], string>({
      query: (userId) => ({
        url: `users/${userId}/documents`,
      }),
      providesTags: ["documents"],
    }),
    updateDocument: builder.mutation<
      Document,
      {
        stateId?: string;
        documentId: string;
        reviewedAt?: Date | null;
        finalizedAt?: Date | undefined;
        lastGeneratedInputHash?: string | null;
        lastGeneratedAt?: Date | null;
      }
    >({
      query: ({
        stateId,
        documentId,
        reviewedAt,
        finalizedAt,
        lastGeneratedInputHash,
        lastGeneratedAt,
      }) => ({
        url: `documents/${documentId}`,
        method: "PATCH",
        body: {
          stateId,
          reviewedAt,
          finalizedAt,
          lastGeneratedInputHash,
          lastGeneratedAt,
        },
      }),
      async onQueryStarted({ stateId }, { dispatch, queryFulfilled }) {
        try {
          if (stateId) {
            await queryFulfilled;
            dispatch(
              questionnaireApi.util.invalidateTags([
                "QuestionnaireSections",
                "QuestionGroup",
              ]),
            );
          }
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["documents", "regeneration-status"],
    }),

    grantApproval: builder.mutation<
      ApprovalGrantSuccessfulResponse,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/approval/grant`,
        method: "POST",
      }),
      invalidatesTags: ["documents"],
    }),
    rescindApproval: builder.mutation<
      ApprovalRescindSuccessfulResponse,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/approval/rescind`,
        method: "POST",
      }),
      invalidatesTags: ["documents"],
    }),
    generateDocument: builder.mutation<Document, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/generate`,
        method: "POST",
      }),
      invalidatesTags: [
        "document-modified",
        "documents",
        "regeneration-status",
      ],
    }),
    createDocumentFile: builder.mutation<
      File,
      { documentId: string; file: DocumentFilePostRequest }
    >({
      query: ({ documentId, file }) => ({
        url: `documents/${documentId}/files`,
        method: "POST",
        body: file,
      }),
      async onQueryStarted({ file }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // If this is a notarization file, invalidate admin users data
          if (file.notarizationId) {
            dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
          }
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (_, __, { file }) => {
        const tags: ("files" | "notarizations")[] = ["files"];
        // If this is a notarization file, also invalidate notarizations
        if (file.notarizationId) {
          tags.push("notarizations");
        }
        return tags;
      },
    }),
    getFilesByDocumentId: builder.query<File[], string>({
      query: (documentId) => ({ url: `documents/${documentId}/files` }),
      providesTags: ["files"],
    }),
    deleteFileByDocumentId: builder.mutation<
      void,
      { fileId: string; documentId: string }
    >({
      query: ({ fileId, documentId }) => ({
        url: `documents/${documentId}/files/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["files", "documents"],
    }),
    getNotarizationsByDocumentId: builder.query<
      DocumentNotarizationsGetResponse,
      string
    >({
      query: (documentId) => ({ url: `documents/${documentId}/notarizations` }),
      providesTags: ["notarizations"],
    }),
    createNotarization: builder.mutation<
      Notarization,
      { documentId: string; notarization: NotarizationPostRequest }
    >({
      query: ({ documentId, notarization }) => ({
        url: `documents/${documentId}/notarizations`,
        method: "POST",
        body: notarization,
      }),
      invalidatesTags: ["notarizations"],
    }),
    createNotarizationProof: builder.mutation<void, { documentId: string }>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/notarizations/proof`,
        method: "POST",
        body: { documentId },
      }),
      invalidatesTags: ["notarizations"],
    }),
    deleteNotarization: builder.mutation<
      void,
      { documentId: string; notarizationId: string }
    >({
      query: ({ documentId, notarizationId }) => ({
        url: `documents/${documentId}/notarizations/${notarizationId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["notarizations"],
    }),
    getInvitesByUserId: builder.query<
      UserInvitesGetResponse,
      { userId: string; templateType: string }
    >({
      query: ({ userId, templateType }) => ({
        url: `users/${userId}/documents/invites`,
        params: { templateType },
      }),
      providesTags: ["invites"],
    }),
    acceptInvite: builder.mutation<
      Document,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `users/${userId}/documents/${documentId}/accept-invite`,
        method: "GET",
      }),
      invalidatesTags: ["invites", "documents"],
    }),

    getDocumentHistoryCommits: builder.query<DocumentHistoryResponse, string>({
      query: (documentId) => ({ url: `documents/${documentId}/history` }),
      providesTags: ["document-history"],
    }),

    getDocumentCommitDetails: builder.query<
      {
        // todo: this should use the backend types, but because the editor uses DocumentWithRelations elsewhere, where the
        // dates are Dates even though they're really strings, it throws off the type checker here and isn't worth fixing currently
        current: { sections: Section[]; clauses: Clause[] };
        previous: { sections: Section[]; clauses: Clause[] };
        diffs: {
          id: string;
          type: "CREATE" | "MODIFY" | "DELETE";
          entity: "section" | "clause";
          moved?: true;
        }[];
      },
      { documentId: string; commitAt: string }
    >({
      query: ({ documentId, commitAt }) => ({
        url: `documents/${documentId}/history/single?commitAt=${encodeURIComponent(commitAt)}`,
      }),
      providesTags: ["document-history"],
    }),

    getDocumentCommitRangeDetails: builder.query<
      // todo: this should use the backend types, but because the editor uses DocumentWithRelations elsewhere, where the
      // dates are Dates even though they're really strings, it throws off the type checker here and isn't worth fixing currently
      {
        current: { sections: Section[]; clauses: Clause[] };
        previous: { sections: Section[]; clauses: Clause[] };
        diffs: {
          id: string;
          type: "CREATE" | "MODIFY" | "DELETE";
          entity: "section" | "clause";
          moved?: true;
        }[];
      },
      { documentId: string; startCommitAt: string; endCommitAt: string }
    >({
      query: ({ documentId, startCommitAt, endCommitAt }) => ({
        url: `documents/${documentId}/history/range?startCommitAt=${encodeURIComponent(startCommitAt)}&endCommitAt=${encodeURIComponent(endCommitAt)}`,
      }),
      providesTags: ["document-history"],
    }),

    resetNotarization: builder.mutation<void, { notarizationId: string }>({
      query: ({ notarizationId }) => ({
        url: `notarizations/${notarizationId}/reset`,
        method: "POST",
      }),
      invalidatesTags: ["notarizations", "documents"],
    }),

    forceResetNotarization: builder.mutation<void, { notarizationId: string }>({
      query: ({ notarizationId }) => ({
        url: `notarizations/${notarizationId}/force-reset`,
        method: "PATCH",
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["notarizations", "documents"],
    }),

    getDocumentParticipants: builder.query<
      DocumentWithRelations & {
        attorneyA?: {
          user: User;
          profilePhotoUrl: string | null;
        } | null;
        attorneyB?: {
          user: User;
          profilePhotoUrl: string | null;
        } | null;
      },
      string
    >({
      query: (documentId) => ({
        url: `documents/${documentId}/participants`,
        method: "GET",
      }),
      providesTags: ["documents"],
    }),
    getRegenerationStatus: builder.query<GetRegenerationStatusResponse, string>(
      {
        query: (documentId) => ({
          url: `documents/${documentId}/regeneration-status`,
        }),
        providesTags: ["regeneration-status"],
      },
    ),
    getResetGenerationEligible: builder.query<
      ResetGenerationEligibleResponse,
      string
    >({
      query: (documentId) => ({
        url: `documents/${documentId}/reset-generation-eligible`,
      }),
      providesTags: ["documents"],
    }),
    resetGeneration: builder.mutation<ResetGenerationResponse, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/reset-generation`,
        method: "POST",
      }),
      invalidatesTags: ["documents", "regeneration-status", "AdminUsers"],
    }),
    clearGenerationTimestamps: builder.mutation<
      ClearGenerationTimestampsResponse,
      { documentId: string; count: number }
    >({
      query: ({ documentId, count }) => ({
        url: `admin/users/documents/${documentId}/clear-generation-timestamps`,
        method: "POST",
        body: { count },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["documents", "AdminUsers"],
    }),
    analyzeLlm: builder.mutation<AnalyzeLlmResponse, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/analyze-llm`,
        method: "POST",
      }),
      invalidatesTags: ["documents", "llm-runs", "AdminUsers"],
    }),
    getLatestLlmRun: builder.query<GetLatestLlmRunResponse, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/llm-runs/latest`,
      }),
      providesTags: ["llm-runs"],
    }),
  }),
});

export const {
  useGetDocumentQuery,
  useGetDocumentsByUserIdQuery,
  useUpdateDocumentMutation,
  useGenerateDocumentMutation,
  useCreateDocumentMutation,
  useCreateDocumentFileMutation,
  useGrantApprovalMutation,
  useRescindApprovalMutation,
  useGetFilesByDocumentIdQuery,
  useDeleteFileByDocumentIdMutation,
  useGetNotarizationsByDocumentIdQuery,
  useCreateNotarizationMutation,
  useDeleteNotarizationMutation,
  useCreateNotarizationProofMutation,
  useGetInvitesByUserIdQuery,
  useAcceptInviteMutation,
  useGetDocumentHistoryCommitsQuery,
  useGetDocumentCommitDetailsQuery,
  useGetDocumentCommitRangeDetailsQuery,
  useResetNotarizationMutation,
  useForceResetNotarizationMutation,
  useGetDocumentParticipantsQuery,
  useGetRegenerationStatusQuery,
  useGetResetGenerationEligibleQuery,
  useResetGenerationMutation,
  useClearGenerationTimestampsMutation,
  useAnalyzeLlmMutation,
  useGetLatestLlmRunQuery,
} = documentsApi;
